import { PARTY_INVITES_PER_MINUTE, PARTY_PENDING_INVITES, PARTY_PROMPTS_PER_MINUTE, PARTY_SIZE, type PartyInvitations } from "./constants.ts";
import type { PartyRefusal } from "./words.ts";

/**
 * The checks an invite meets (ADR-101 decision 4), in order, over plain facts the gateway reads from its registry and
 * the store. Pure: no clock, no store, no names.
 */

const MINUTE_MS = 60_000;

/**
 * What the checks read. Counts and waits are the gateway's reading at the time of the check.
 *
 * `windowWaitMs` is the wait until the connection's party request window reopens, 0 while it is open;
 * `accountInviteTimes` the times in milliseconds since the epoch of the account's recent invites; `muted` a mute in
 * scope `all`; `mayInvite` the inviter's right (the leader's, or a member's grant); `invitedFound` a character of
 * that name in the world; `inviterBlocked` and `invitedBlocked` a block by the inviter's family of the invited's and
 * the reverse; `seatedBlocked` one entry a seated member, whether its family and the invited's have blocked each
 * other in either direction; `admitted` the invited's setting admits the inviter; `self` the invited is the inviter;
 * `invitedSeated` the invited is in a party; `seated` the members seated, pending invites not counted;
 * `pendingFromParty` an invite from this party to the invited is pending; `recentUntil` the end of the wait running
 * from the inviter's account toward the invited, 0 for none; `promptsLastMinute` and `promptsResetMs` the prompts the
 * invited was offered in the current minute and the time until that count resets; `pendingToInvited` and
 * `pendingOfParty` the invites pending to the invited and from the party, `pendingFreeMs` the time until the soonest
 * of them lapses; `combat` the invited is in combat with a seated member.
 */
export interface InviteFacts {
  readonly windowWaitMs: number;
  readonly accountInviteTimes: readonly number[];
  readonly muted: boolean;
  readonly mayInvite: boolean;
  readonly invitedFound: boolean;
  readonly inviterBlocked: boolean;
  readonly invitedBlocked: boolean;
  readonly seatedBlocked: readonly boolean[];
  readonly admitted: boolean;
  readonly self: boolean;
  readonly invitedSeated: boolean;
  readonly seated: number;
  readonly pendingFromParty: boolean;
  readonly recentUntil: number;
  readonly promptsLastMinute: number;
  readonly promptsResetMs: number;
  readonly pendingToInvited: number;
  readonly pendingOfParty: number;
  readonly pendingFreeMs: number;
  readonly combat: boolean;
}

/**
 * A refused invite: the reason and the wait, 0 for a reason with none.
 */
export interface InviteRefusal {
  readonly reason: PartyRefusal;
  readonly waitMs: number;
}

/**
 * The wait before an account's invites reopen: the time until the oldest of its invites in the last minute leaves it,
 * once it has sent `PARTY_INVITES_PER_MINUTE` in that minute.
 *
 * @param times The times of the account's invites.
 * @param now The time in milliseconds since the epoch.
 * @returns 0 while under the limit, else the milliseconds to wait.
 */
function inviteLimitWait(times: readonly number[], now: number): number {
  if (times.length < PARTY_INVITES_PER_MINUTE) {
    return 0;
  }
  const sorted = [...times].sort((x, y) => x - y);
  return Math.max(0, (sorted[sorted.length - PARTY_INVITES_PER_MINUTE] as number) + MINUTE_MS - now);
}

/**
 * The first check an invite fails, decision 4's order: the request window and the account's limit (`rate`), a mute
 * (`muted`), the inviter's right (`leader`), the one reason `cannot` for a missing name, a block either way, a block
 * with any seated member or a setting that excludes the inviter, then the invited being the inviter (`self`), in a
 * party (`member`), a full party (`full`), already invited (`pending`), a wait toward it (`recent`), too many prompts
 * or pending invites (`busy`) and a fight with a seated member (`combat`).
 *
 * @param facts What the checks read.
 * @param now The time in milliseconds since the epoch.
 * @returns The refusal, or null when the invite may go ahead.
 */
export function inviteRefusal(facts: InviteFacts, now: number): InviteRefusal | null {
  const rateWait = Math.max(facts.windowWaitMs, inviteLimitWait(facts.accountInviteTimes, now));
  if (rateWait > 0) {
    return { reason: "rate", waitMs: rateWait };
  }
  if (facts.muted) {
    return { reason: "muted", waitMs: 0 };
  }
  if (!facts.mayInvite) {
    return { reason: "leader", waitMs: 0 };
  }
  if (!facts.invitedFound || facts.inviterBlocked || facts.invitedBlocked || facts.seatedBlocked.some(Boolean) || !facts.admitted) {
    return { reason: "cannot", waitMs: 0 };
  }
  if (facts.self) {
    return { reason: "self", waitMs: 0 };
  }
  if (facts.invitedSeated) {
    return { reason: "member", waitMs: 0 };
  }
  if (facts.seated >= PARTY_SIZE) {
    return { reason: "full", waitMs: 0 };
  }
  if (facts.pendingFromParty) {
    return { reason: "pending", waitMs: 0 };
  }
  if (facts.recentUntil > now) {
    return { reason: "recent", waitMs: facts.recentUntil - now };
  }
  if (facts.promptsLastMinute >= PARTY_PROMPTS_PER_MINUTE) {
    return { reason: "busy", waitMs: facts.promptsResetMs };
  }
  if (facts.pendingToInvited >= PARTY_PENDING_INVITES || facts.pendingOfParty >= PARTY_PENDING_INVITES) {
    return { reason: "busy", waitMs: facts.pendingFreeMs };
  }
  return facts.combat ? { reason: "combat", waitMs: 0 } : null;
}

/**
 * How the inviter stands toward the invited, as the gateway reads it: on the same map, a friend family (Bible 9.3) and
 * a friend family in common.
 */
export interface InviterRelation {
  readonly sameMap: boolean;
  readonly friends: boolean;
  readonly sharedFriend: boolean;
}

/**
 * Whether the invited's "Party invitations" setting admits the inviter (decision 6).
 *
 * @param setting The invited's setting.
 * @param relation How the inviter stands toward the invited.
 * @returns True when the inviter may invite it.
 */
export function admitsInviter(setting: PartyInvitations, relation: InviterRelation): boolean {
  switch (setting) {
    case "map":
      return relation.sameMap;
    case "friends":
      return relation.friends;
    case "friendsOfFriends":
      return relation.friends || relation.sharedFriend;
    case "nobody":
      return false;
  }
}
